import { useState, useEffect } from 'react'
import { WifiOff, Loader2, ShieldAlert } from 'lucide-react'
import { useApiStatus } from '@/hooks/use-api-status'
import { useSessionCountdown } from '@/hooks/use-session-countdown'
import { api } from '@/api/client'
import { cn } from '@/lib/utils'

type OverlayState = 'hidden' | 'go-online' | 'reconnecting' | 're-login'

export function SessionExpiredOverlay() {
  const { connected, sessionDead, readOnly, pendingSync } = useApiStatus()
  const { expired } = useSessionCountdown()
  const [dismissed, setDismissed] = useState(false)
  const [state, setState] = useState<OverlayState>('hidden')

  useEffect(() => {
    // State machine transitions
    if (sessionDead && connected) {
      // Online but session rejected → State 3: re-login
      setState('re-login')
      setDismissed(false)
      return
    }

    if (!connected && expired) {
      if (dismissed) {
        // State 1 was dismissed - but if we just came back online, re-show
        setState('hidden')
        return
      }
      // Offline + grace expired → State 1: go online
      setState('go-online')
      return
    }

    if (connected && (expired || readOnly)) {
      // Just came back online from an expired offline state → State 2: reconnecting
      setState('reconnecting')
      setDismissed(false)
      return
    }

    // Everything is fine
    if (connected && !sessionDead && !expired && !readOnly) {
      setState('hidden')
      setDismissed(false)
    }
  }, [connected, expired, sessionDead, readOnly, dismissed])

  const handleDismiss = () => {
    setDismissed(true)
    api.setReadOnly(true)
    setState('hidden')
  }

  const handleSignIn = () => {
    // Open OAuth in a popup
    const oauthUrl = `${api.apiOrigin}/auth/google`
    const popup = window.open(oauthUrl, 'ledger-reauth', 'width=500,height=700,popup=yes')

    if (!popup) {
      // Popup blocked - fall back to redirect
      window.location.href = oauthUrl
      return
    }

    // Listen for the callback via message or polling
    const checkClosed = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkClosed)
        // Check if we got a new token (the callback page should have set it)
        const token = localStorage.getItem('ledger_core_token')
        if (token) {
          api.onReauthenticated(token)
        }
      }
    }, 500)
  }

  if (state === 'hidden') return null

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className={cn(
        'mx-4 w-full max-w-md rounded-lg border p-6 shadow-xl',
        state === 'go-online' && 'border-red-200 bg-card dark:border-red-800',
        state === 'reconnecting' && 'border-amber-200 bg-card dark:border-amber-800',
        state === 're-login' && 'border-amber-200 bg-card dark:border-amber-800',
      )}>
        {/* State 1: Go online (dismissable) */}
        {state === 'go-online' && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                <WifiOff className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <h2 className="text-lg font-semibold text-foreground">Session Expired</h2>
            </div>
            <p className="text-sm text-muted-foreground">
              Your offline session has expired after 7 days. Please reconnect to the internet
              to sync your changes.
            </p>
            <p className="text-xs text-muted-foreground/80">
              Your data is still saved locally and will sync once you&apos;re signed in again.
            </p>
            {pendingSync > 0 && (
              <p className="text-xs font-medium text-amber-600 dark:text-amber-400">
                {pendingSync} change{pendingSync !== 1 ? 's' : ''} waiting to sync
              </p>
            )}
            <button
              type="button"
              onClick={handleDismiss}
              className="w-full rounded-md border border-border bg-muted/50 px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted"
            >
              View cached data
            </button>
          </div>
        )}

        {/* State 2: Reconnecting (not dismissable) */}
        {state === 'reconnecting' && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30">
                <Loader2 className="h-5 w-5 animate-spin text-amber-600 dark:text-amber-400" />
              </div>
              <h2 className="text-lg font-semibold text-foreground">Reconnecting…</h2>
            </div>
            <p className="text-sm text-muted-foreground">
              Restoring your session…
            </p>
          </div>
        )}

        {/* State 3: Re-login required (not dismissable) */}
        {state === 're-login' && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30">
                <ShieldAlert className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </div>
              <h2 className="text-lg font-semibold text-foreground">Sign in to continue</h2>
            </div>
            <p className="text-sm text-muted-foreground">
              Your session has expired. Please sign in again to sync your offline changes.
              Your data is safe - nothing has been lost.
            </p>
            {pendingSync > 0 && (
              <p className="text-xs font-medium text-amber-600 dark:text-amber-400">
                {pendingSync} change{pendingSync !== 1 ? 's' : ''} waiting to sync
              </p>
            )}
            <button
              type="button"
              onClick={handleSignIn}
              className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
            >
              Sign in
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
